import React, {useState} from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {Checkbox} from '../checkboxes/Checkbox';
import {SimpleButton} from '../button/SimpleButton';

export interface Occurrence {
  id: string;
  startDateTime: Date;
  endDateTime: Date;
}

interface RecurrenceSectionProps {
  startDate: Date | null;
  endDate: Date | null;
  initialOccurrences?: Occurrence[];
  disabled?: boolean;
  /**
   * Called whenever the effective occurrences list changes.
   * - `null`  → recurrence is disabled
   * - `[]`    → recurrence is enabled but not yet generated
   * - `[...]` → generated occurrences (may be trimmed by the user)
   */
  onChange: (occurrences: Occurrence[] | null) => void;
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

const occLabelStyle: React.CSSProperties = {
  display: 'block',
  color: '#666',
  fontSize: '0.68rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 4,
};

export const RecurrenceSection: React.FC<RecurrenceSectionProps> = ({
  startDate,
  endDate,
  initialOccurrences,
  disabled,
  onChange,
}) => {
  const hasInitial = !!initialOccurrences && initialOccurrences.length > 0;
  const [isEnabled, setIsEnabled] = useState(hasInitial);
  const [count, setCount] = useState(hasInitial ? String(initialOccurrences!.length) : '');
  const [error, setError] = useState<string | null>(null);
  const [occurrences, setOccurrences] = useState<Occurrence[]>(initialOccurrences ?? []);

  // Re-sync when initialOccurrences changes (loads async or navigates to different event)
  React.useEffect(() => {
    if (initialOccurrences && initialOccurrences.length > 0) {
      setIsEnabled(true);
      setCount(String(initialOccurrences.length));
      setOccurrences(initialOccurrences);
      onChange(initialOccurrences);
    } else {
      setIsEnabled(false);
      setCount('');
      setOccurrences([]);
      onChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOccurrences]);

  const handleGenerate = () => {
    setError(null);

    if (!startDate || isNaN(startDate.getTime())) {
      setError('Please set a valid Start Date first.');
      return;
    }
    if (!endDate || isNaN(endDate.getTime())) {
      setError('Please set a valid End Date first.');
      return;
    }

    const parsed = parseInt(count, 10);
    if (!count || isNaN(parsed) || parsed < 1) {
      setError('Enter a valid number of occurrences (min 1).');
      return;
    }
    if (parsed > 52) {
      setError('Maximum 52 occurrences allowed (1 year).');
      return;
    }

    const generated: Occurrence[] = Array.from({length: parsed}, (_, i) => ({
      id: `occ-${Date.now()}-${i}`,
      startDateTime: new Date(startDate.getTime() + i * MS_PER_WEEK),
      endDateTime: new Date(endDate.getTime() + i * MS_PER_WEEK),
    }));

    setOccurrences(generated);
    onChange(generated);
  };

  const handleRemove = (id: string) => {
    const updated = occurrences.filter(o => o.id !== id);
    setOccurrences(updated);
    setCount(String(updated.length));
    onChange(updated);
  };

  const handleUpdate = (
    id: string,
    field: 'startDateTime' | 'endDateTime',
    date: Date | null,
  ) => {
    if (!date) return;
    const updated = occurrences.map(o =>
      o.id === id ? {...o, [field]: date} : o,
    );
    setOccurrences(updated);
    onChange(updated);
  };

  const handleAddNext = () => {
    const ref = occurrences[occurrences.length - 1];
    if (!ref) return;
    const newOcc: Occurrence = {
      id: `occ-${Date.now()}`,
      startDateTime: new Date(ref.startDateTime.getTime() + MS_PER_WEEK),
      endDateTime: new Date(ref.endDateTime.getTime() + MS_PER_WEEK),
    };
    const updated = [...occurrences, newOcc];
    setOccurrences(updated);
    setCount(String(updated.length));
    onChange(updated);
  };

  return (
    <div style={{marginBottom: isEnabled ? '32px' : 0}}>
      <Checkbox
        label="Repeat Weekly"
        checked={isEnabled}
        disabled={disabled}
        disabledTooltip="You cannot change recurrence when editing. Please delete the event and create a new one."
        onChange={(next: boolean) => {
          setIsEnabled(next);
          setCount('');
          setError(null);
          setOccurrences([]);
          onChange(next ? [] : null);
        }}
      />

      {/* Expanded content */}
      {isEnabled && (
        <div
          style={{
            marginTop: 12,
            animation: 'recurrence-expand 0.25s ease forwards',
          }}>
          <style>{`
            @keyframes recurrence-expand {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
          {/* Count input + Generate button */}
          <div style={{display: 'flex', gap: 10, alignItems: 'center'}}>
            <input
              type="number"
              min={1}
              max={52}
              value={count}
              placeholder="Number of occurrences"
              onChange={e => {
                setCount(e.target.value);
                setError(null);
              }}
              style={{
                flex: 1,
                height: '46px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${error ? '#ff5555' : '#333'}`,
                backgroundColor: '#2a2a2a',
                color: '#fff',
                fontSize: '0.85rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <SimpleButton text="Generate" onClick={handleGenerate} disabled={disabled || occurrences.length > 0} />
          </div>

          {/* Inline error */}
          {error && (
            <div
              style={{color: '#ff5555', fontSize: '0.78rem', marginTop: 6}}>
              {error}
            </div>
          )}

          {/* Occurrence list */}
          {occurrences.length > 0 && (
            <>
              <style>{`
                .occ-datepicker-input {
                  width: 100%;
                  height: 38px;
                  padding: 0 12px;
                  border-radius: 6px;
                  border: 1px solid #333;
                  background-color: #1e1e1e;
                  color: #fff;
                  font-size: 0.8rem;
                  outline: none;
                  box-sizing: border-box;
                  cursor: pointer;
                  transition: border-color 0.2s ease;
                }
                .occ-datepicker-input:hover { border-color: #daff00; }
                .occ-datepicker-input:focus { border-color: #daff00; }
                .react-datepicker-popper { z-index: 9999 !important; }
              `}</style>

              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}>
                {occurrences.map((occ, index) => (
                  <div
                    key={occ.id}
                    style={{
                      padding: '12px 14px',
                      backgroundColor: '#2a2a2a',
                      borderRadius: '8px',
                      border: '1px solid #444',
                    }}>
                    {/* Header row */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 10,
                      }}>
                      <span
                        style={{
                          color: '#daff00',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                        }}>
                        #{index + 1}
                      </span>
                      <div style={{display: 'flex', gap: 6}}>
                        <button
                          type="button"
                          title="Remove this occurrence"
                          onClick={() => handleRemove(occ.id)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #555',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            color: '#888',
                            fontSize: '1.1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            padding: 0,
                            transition: 'color 0.15s, border-color 0.15s',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.borderColor = '#888';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.color = '#888';
                            e.currentTarget.style.borderColor = '#555';
                          }}>
                          ×
                        </button>
                      </div>
                    </div>

                    {/* Editable date pickers */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}>
                      <div>
                        <label style={occLabelStyle}>Start</label>
                        <DatePicker
                          selected={occ.startDateTime}
                          onChange={(date: Date | null) =>
                            handleUpdate(occ.id, 'startDateTime', date)
                          }
                          showTimeSelect
                          dateFormat="EEE, d MMM yyyy HH:mm"
                          timeIntervals={5}
                          autoComplete="off"
                          className="occ-datepicker-input"
                          portalId="root"
                        />
                      </div>
                      <div>
                        <label style={occLabelStyle}>End</label>
                        <DatePicker
                          selected={occ.endDateTime}
                          onChange={(date: Date | null) =>
                            handleUpdate(occ.id, 'endDateTime', date)
                          }
                          showTimeSelect
                          dateFormat="EEE, d MMM yyyy HH:mm"
                          timeIntervals={5}
                          autoComplete="off"
                          className="occ-datepicker-input"
                          portalId="root"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add next week button — always at the bottom */}
              <div style={{marginTop: 8}}>
                <SimpleButton text="+ Add next week" onClick={handleAddNext} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
